/**
 * IndexedDB Manager for Video Chunks
 * Stores chunks locally for resilience and auto-resume
 */

const DB_NAME = 'ExamRecordingDB';
const DB_VERSION = 1;
const STORE_NAME = 'videoChunks';

class ChunkStorage {
  constructor() {
    this.db = null;
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          
          // Create indexes
          objectStore.createIndex('recording_id', 'recording_id', { unique: false });
          objectStore.createIndex('video_type', 'video_type', { unique: false });
          objectStore.createIndex('uploaded', 'uploaded', { unique: false });
          objectStore.createIndex('chunk_index', 'chunk_index', { unique: false });
          
          console.log('✅ IndexedDB object store created');
        }
      };
    });
  }

  /**
   * Save a chunk to IndexedDB
   */
  async saveChunk(recordingId, videoType, chunkIndex, blob, timestamp) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const chunkData = {
        id: `${recordingId}_${videoType}_${chunkIndex}`,
        recording_id: recordingId,
        video_type: videoType,
        chunk_index: chunkIndex,
        blob: blob,
        size: blob.size,
        timestamp: timestamp,
        uploaded: false,
        saved_at: new Date().toISOString(),
        retry_count: 0
      };

      const request = store.put(chunkData);

      request.onsuccess = () => {
        console.log(`💾 Chunk ${chunkIndex} saved to IndexedDB (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
        resolve(chunkData);
      };

      request.onerror = () => {
        console.error('❌ Failed to save chunk:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Mark chunk as uploaded
   */
  async markChunkUploaded(recordingId, videoType, chunkIndex) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const id = `${recordingId}_${videoType}_${chunkIndex}`;
      const request = store.get(id);

      request.onsuccess = () => {
        const chunk = request.result;
        if (chunk) {
          chunk.uploaded = true;
          chunk.uploaded_at = new Date().toISOString();
          
          const updateRequest = store.put(chunk);
          updateRequest.onsuccess = () => {
            console.log(`✅ Chunk ${chunkIndex} marked as uploaded`);
            resolve(chunk);
          };
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all pending (non-uploaded) chunks for a recording
   */
  async getPendingChunks(recordingId, videoType) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('recording_id');
      
      const request = index.getAll(recordingId);

      request.onsuccess = () => {
        const allChunks = request.result;
        const pending = allChunks
          .filter(chunk => chunk.video_type === videoType && !chunk.uploaded)
          .sort((a, b) => a.chunk_index - b.chunk_index);
        
        console.log(`📊 Found ${pending.length} pending chunks for ${recordingId}/${videoType}`);
        resolve(pending);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all chunks (uploaded and pending)
   */
  async getAllChunks(recordingId, videoType) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('recording_id');
      
      const request = index.getAll(recordingId);

      request.onsuccess = () => {
        const allChunks = request.result;
        const filtered = allChunks
          .filter(chunk => chunk.video_type === videoType)
          .sort((a, b) => a.chunk_index - b.chunk_index);
        
        resolve(filtered);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Increment retry count for a chunk
   */
  async incrementRetryCount(recordingId, videoType, chunkIndex) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const id = `${recordingId}_${videoType}_${chunkIndex}`;
      const request = store.get(id);

      request.onsuccess = () => {
        const chunk = request.result;
        if (chunk) {
          chunk.retry_count = (chunk.retry_count || 0) + 1;
          chunk.last_retry_at = new Date().toISOString();
          
          const updateRequest = store.put(chunk);
          updateRequest.onsuccess = () => resolve(chunk.retry_count);
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(0);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete all chunks for a recording (cleanup after successful upload)
   */
  async deleteRecording(recordingId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('recording_id');
      
      const request = index.openCursor(IDBKeyRange.only(recordingId));
      let deleteCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deleteCount++;
          cursor.continue();
        } else {
          console.log(`🧹 Deleted ${deleteCount} chunks for recording ${recordingId}`);
          resolve(deleteCount);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get storage statistics
   */
  async getStats() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.getAll();

      request.onsuccess = () => {
        const chunks = request.result;
        
        const stats = {
          total_chunks: chunks.length,
          uploaded_chunks: chunks.filter(c => c.uploaded).length,
          pending_chunks: chunks.filter(c => !c.uploaded).length,
          total_size: chunks.reduce((sum, c) => sum + c.size, 0),
          recordings: [...new Set(chunks.map(c => c.recording_id))].length
        };
        
        resolve(stats);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data (for testing/debugging)
   */
  async clearAll() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.clear();

      request.onsuccess = () => {
        console.log('🧹 All chunks cleared from IndexedDB');
        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export default new ChunkStorage();
